/**
 * Per-user PTZ preferences (US-PTZ-Buffer-01). Mirrors the Option-3 hybrid
 * pattern already proven for the Live View preview prefs:
 *
 *   - Initial render: read the per-user `localStorage` cache (instant).
 *   - On mount: GET `/me/preferences/ptz` and reconcile with the cache.
 *   - On user mutation: write cache immediately, debounce PUT to the server.
 *   - On logout: cache is wiped via `clearCachedPtzPrefs(null)` so the next
 *     user on the same browser does not inherit the previous cap.
 *
 * Kept in its own module (rather than extending `previewSession.ts`) so the
 * preview session and PTZ prefs evolve independently — they have different
 * lifecycles (preview is per-stage geometry, PTZ is per-input behaviour).
 */

const PTZ_PREFS_CACHE_PREFIX = "omni_watch:prefs:ptz:";

/**
 * Hard envelope for the buffered-click cap. Mirrors the server's
 * `PTZ_BUFFER_MAX_MIN/MAX` so client and server agree on what a "valid"
 * value looks like — the server is still authoritative; the client clamp is
 * defense-in-depth and a UX hint (the slider shows the same range).
 */
export const PTZ_BUFFER_MAX_MIN = 1;
export const PTZ_BUFFER_MAX_MAX = 10;
export const PTZ_BUFFER_MAX_DEFAULT = 10;

export type PtzPrefs = {
  bufferMax: number;
};

export const DEFAULT_PTZ_PREFS: PtzPrefs = {
  bufferMax: PTZ_BUFFER_MAX_DEFAULT,
};

function clampBufferMax(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return PTZ_BUFFER_MAX_DEFAULT;
  }
  const rounded = Math.round(value);
  return Math.min(PTZ_BUFFER_MAX_MAX, Math.max(PTZ_BUFFER_MAX_MIN, rounded));
}

/**
 * Narrow a possibly-corrupt cache blob into a fully-clamped `PtzPrefs`.
 * Out-of-range or missing fields fall back to the documented default so a
 * partially filled blob never crashes the UI.
 */
export function normalizePtzPrefs(raw: unknown): PtzPrefs {
  const o = (raw && typeof raw === "object" ? raw : {}) as { bufferMax?: unknown };
  return { bufferMax: clampBufferMax(o.bufferMax) };
}

function cacheKey(userId: string): string {
  return `${PTZ_PREFS_CACHE_PREFIX}${userId}`;
}

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getCachedPtzPrefs(userId: string | null | undefined): PtzPrefs {
  if (!userId) return { ...DEFAULT_PTZ_PREFS };
  const raw = lsGet(cacheKey(userId));
  if (!raw) return { ...DEFAULT_PTZ_PREFS };
  try {
    return normalizePtzPrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_PTZ_PREFS };
  }
}

export function setCachedPtzPrefs(userId: string | null | undefined, prefs: PtzPrefs): void {
  if (!userId) return;
  lsSet(cacheKey(userId), JSON.stringify(normalizePtzPrefs(prefs)));
}

/**
 * Wipe the cached PTZ prefs. Pass `null` on logout to sweep every cached
 * user (mirrors `clearCachedPreviewPrefs` so a shared browser doesn't leak
 * settings across accounts).
 */
export function clearCachedPtzPrefs(userId: string | null | undefined): void {
  if (userId) {
    lsRemove(cacheKey(userId));
    return;
  }
  try {
    const toDrop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PTZ_PREFS_CACHE_PREFIX)) toDrop.push(k);
    }
    for (const k of toDrop) lsRemove(k);
  } catch {
    /* ignore */
  }
}
